import React, { useState } from 'react';
import { Check, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const ComparisonTable = ({ category, optionA, optionB, itemsA, itemsB, summary, explanation, colorA = 'turquoise', colorB = 'pink', iconA = '', iconB = '' }) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const getColorClass = (color, type) => {
    if (type === 'header') {
      return color === 'turquoise' ? 'bg-turquoise-50 border-turquoise-300' : 'bg-pink-50 border-pink-300';
    }
    return color === 'turquoise' ? 'text-turquoise-600' : 'text-pink-600';
  };

  const getTooltipContent = () => {
    switch (category) {
      case 'Logic':
        return (
          <div className="space-y-2">
            <p className="font-semibold">The practical side of your decision:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Pros and cons of each option</li>
              <li>Financial considerations</li>
              <li>Time and resource requirements</li>
              <li>Expert advice or recommendations</li>
              <li>Facts and tangible consequences</li>
            </ul>
            <p className="text-sm italic mt-2">This is the head speaking - what makes rational sense?</p>
          </div>
        );
      case 'Universe':
        return (
          <div className="space-y-2">
            <p className="font-semibold">Signs and synchronicities showing up around your decision:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Recurring themes you keep noticing</li>
              <li>"Coincidences" that feel meaningful</li>
              <li>Dreams or symbols that catch your attention</li>
              <li>Perfect (or terrible) timing of events</li>
              <li>Things that make you go "huh, interesting..."</li>
              <li>Numbers, songs, conversations that repeat</li>
            </ul>
            <p className="text-sm italic mt-2">The universe speaks through patterns. What keeps showing up?</p>
          </div>
        );
      case 'Vibe':
        return (
          <div className="space-y-2">
            <p className="font-semibold">Your body's wisdom about each option:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Does thinking about it make you feel lighter or heavier?</li>
              <li>Excited or full of dread?</li>
              <li>Expansive or contracted?</li>
              <li>Energized or drained?</li>
              <li>Peace or tension?</li>
            </ul>
            <p className="text-sm italic mt-2">Notice the physical response in your body, not just your thoughts. Your body knows before your mind does.</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="comparison-table-container">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-2xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {category}
        </h3>
        <TooltipProvider>
          <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
            <TooltipTrigger asChild>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  setTooltipOpen(!tooltipOpen);
                }}
                className="flex items-center justify-center w-5 h-5 rounded-full border-2 border-purple-500 text-purple-500 hover:border-purple-600 hover:text-purple-600 transition-colors text-xs font-semibold"
              >
                ?
              </button>
            </TooltipTrigger>
            <TooltipContent 
              className="max-w-sm bg-purple-50 rounded-lg shadow-lg p-4"
              side="right"
              align="start"
              sideOffset={10}
            >
              {getTooltipContent()}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      {/* Explanation */}
      {explanation && (
        <p className="text-gray-600 text-sm mb-4">{explanation}</p>
      )}
      
      <div className="grid grid-cols-2 gap-4">
        {/* Option A Column */}
        <div className="comparison-column border-2 border-gray-200">
          <div className={`column-header ${getColorClass(colorA, 'header')}`}>
            <h4 className="text-lg font-semibold text-white flex items-center justify-center gap-2">
              {iconA && <span className="text-2xl">{iconA}</span>}
              <span>{optionA}</span>
            </h4>
          </div>
          <div className="column-content">
            {itemsA && itemsA.length > 0 ? (
              itemsA.map((item, index) => {
                const isPositive = item.type === 'pro' || item.type === 'positive';
                const isNeutral = item.type === 'neutral';
                return (
                  <div key={index} className={`comparison-item ${isPositive ? 'item-positive' : (isNeutral ? '' : 'item-negative')}`}>
                    {isNeutral ? null : (
                      isPositive ? (
                        <Check className="w-5 h-5 text-turquoise-600 flex-shrink-0" data-testid="check-icon" />
                      ) : (
                        <X className="w-5 h-5 text-pink-600 flex-shrink-0" data-testid="x-icon" />
                      )
                    )}
                    <span className={isNeutral ? "text-gray-400 italic text-sm" : "text-gray-700"}>{item.text}</span>
                  </div>
                );
              })
            ) : (
              <div className="text-gray-400 italic text-sm">(no signs mentioned)</div>
            )}
          </div>
        </div>

        {/* Option B Column */}
        <div className="comparison-column border-2 border-gray-200">
          <div className={`column-header ${getColorClass(colorB, 'header')}`}>
            <h4 className="text-lg font-semibold text-white flex items-center justify-center gap-2">
              {iconB && <span className="text-2xl">{iconB}</span>}
              <span>{optionB}</span>
            </h4>
          </div>
          <div className="column-content">
            {itemsB && itemsB.length > 0 ? (
              itemsB.map((item, index) => {
                const isPositive = item.type === 'pro' || item.type === 'positive';
                const isNeutral = item.type === 'neutral';
                return (
                  <div key={index} className={`comparison-item ${isPositive ? 'item-positive' : (isNeutral ? '' : 'item-negative')}`}>
                    {isNeutral ? null : (
                      isPositive ? (
                        <Check className="w-5 h-5 text-turquoise-600 flex-shrink-0" data-testid="check-icon" />
                      ) : (
                        <X className="w-5 h-5 text-pink-600 flex-shrink-0" data-testid="x-icon" />
                      )
                    )}
                    <span className={isNeutral ? "text-gray-400 italic text-sm" : "text-gray-700"}>{item.text}</span>
                  </div>
                );
              })
            ) : (
              <div className="text-gray-400 italic text-sm">(no signs mentioned)</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonTable;
